<template>
    <BasePanel
        headline="lang_LABEL_CONTRACTS_PANEL_CAPTION_COSTS"
        :alvqa="alvqa"
    >
        <template v-slot:panelContent>
            <v-data-table
                :headers="headers"
                :items="listItems"
                hide-default-footer
                :header-props="{ sortIcon: '' }"
                :loading="isLoading"
                :items-per-page="300"
                :page.sync="currentPage"
                :server-items-length="totalRecords"
                :no-data-text="$t('lang_GENERAL_MESSAGE_NO_RECORDS_FOUND')"
                class="noMinWidth"
            >
            <!-- TOP SLOT -->
                <template v-slot:top>
                    <v-row class="pb-3 pl-4">
                        <v-spacer></v-spacer>
                        <v-col 
                            cols="3"
                            align-self="end"
                        >
                            <BaseButton 
                                @click="addNewRow"
                                :disabled="$route.params.alvId === 'new'"
                                label="lang_GENERAL_BUTTON_ADD_ROW"
                                block
                                :alvqa="alvqa + '-button-add-row'"
                            />
                        </v-col>
                    </v-row>
                </template>

            <!-- Headers -->
                <template v-for="(header, i) in headers" v-slot:[`header.${header.value}`]>
                    <span :alvqa="header.alvqa" :key="i">{{ header.text }}</span>
                </template>

            <!-- Items -->
                <template v-slot:[`item.costGroup`]="{ item }">
                    <div class="ellipsis">
                        <BaseTooltip 
                            :simpleText="getCostGroup(item.costGroup)"
                            :alvqa="alvqa + '-list-row-text-cost-group'"
                        />
                    </div>
                </template>
                <template v-slot:[`item.date`]="{ item }">
                    <div 
                        class="text-right"
                        :alvqa="alvqa + '-list-row-text-date'"
                    >
                        {{ item.date !== null && item.date !== "0000-00-00" ? $d(new Date(item.date), 'alvDateFormat') : '' }}
                    </div>
                </template>
                <template v-slot:[`item.recipient`]="{ item }">
                    <div class="ellipsis">
                        <BaseTooltip 
                            :simpleText="item.recipient !== null ? item.recipient.gemaName : ''"
                            :alvqa="alvqa + '-list-row-text-recipient'"
                        />
                    </div>
                </template>
                <template v-slot:[`item.description`]="{ item }">
                    <div class="ellipsis">
                        <BaseTooltip 
                            :simpleText="item.description"
                            :alvqa="alvqa + '-list-row-text-description'"
                        />
                    </div>
                </template>
                <template v-slot:[`item.amount`]="{ item }">
                    <div 
                        class="text-right"
                        :alvqa="alvqa + '-list-row-text-amount'"
                    >                        
                        {{ $n(item.amount, 'number') }}
                    </div>
                </template>
                <template v-slot:[`item.documentNumber`]="{ item }">
                    <div class="ellipsis">
                        <BaseTooltip 
                            :simpleText="item.documentNumber"
                            :alvqa="alvqa + '-list-row-text-document-number'"
                        />
                    </div>
                </template>
                <template v-slot:[`item.country`]="{ item }">
                    <div>
                        <BaseTooltip>
                            <template v-slot:content>
                                <div 
                                    :alvqa="alvqa + '-list-row-text-country'"
                                >{{ item.country }}</div>
                            </template>
                            <template v-slot:text>{{ $alvPlugin.getTranslatedCountryName(item.country) }}</template>
                        </BaseTooltip>
                    </div>
                </template>
                <template v-slot:[`item.paidOn`]="{ item }">
                    <div 
                        class="text-right"
                        :alvqa="alvqa + '-list-row-text-paid-on'"
                    >
                        {{ item.paidOn !== null ? $d(new Date(item.paidOn), 'alvDateFormat') : '' }}
                    </div>
                </template>
                <template v-slot:[`item.accountingPeriod`]="{ item }">
                    <div 
                        class="text-right"
                        :alvqa="alvqa + '-list-row-text-accounting-period'"
                    >
                        {{ $alvPlugin.displayPeriod(item.accountingPeriod) }}
                    </div>
                </template>
                <template v-slot:[`item.split`]="{ item }">
                    <div 
                        class="text-right"
                        :alvqa="alvqa + '-list-row-text-split'"
                    >                        
                        {{ $alvPlugin.getNumberLocalization(item.split, 'number') }} 
                    </div>
                </template>
                <template v-slot:[`item.assignToRelease`]="{ item }">
                    <div class="ellipsis">
                        <BaseTooltip 
                            :simpleText="getAssignToRelease(item.assignToRelease)"
                            :alvqa="alvqa + '-list-row-text-assign-to-release'"
                        />
                    </div>
                </template>

            <!-- Buttons -->
                <template v-slot:[`item.inlineEdit`]="{ item }">
                    <BaseIcon   
                        :alvqa="alvqa + '-list-row-icon-inline-edit'"
                        icon="edit" 
                        tooltipText="lang_GENERAL_CAPTION_ICON_EDIT"   
                        @click.stop="editInlineFunction(item)"
                    />
                </template>
                <template v-slot:[`item.delete`]="{ item }">
                    <BaseIcon 
                        @click="deleteRow(item)"
                        icon="delete"
                        color="iconDelete"
                        tooltipText="lang_GENERIC_CAPTION_DELETE"
                        :alvqa="alvqa + 'list-row-icon-delete'"
                    />
                </template>
            </v-data-table>
            <v-pagination   
                v-if="numberOfPages > 1"
                total-visible="10" 
                v-model="currentPage" 
                :length="numberOfPages"
                color="pagination"
                @input="nextPage()"
            ></v-pagination>
        </template>
    </BasePanel>
</template>

<script>
import { DropdownLabelReleasesBudgetGroupTypes } from '@/assets/dropdowns/alvDropdowns.js'

import BasePanel from '@/components/_base/BasePanel'
import BaseTooltip from '@/components/_base/BaseTooltip'
import BaseButton from '@/components/_buttons/BaseButton'
import BaseIcon from '@/components/_buttons/BaseIcon'

export default {
    name: "LabelContractsCostsEditPanel",
    components: {
        BasePanel,
        BaseTooltip,
        BaseButton,
        BaseIcon
    },
    data() {
        return {
            alvqa: 'contracts-label-edit-form-panel-costs'
        }
    },
    computed: {
        headers() {
            return [
                {
                    text: this.$t("lang_LABEL_CONTRACTS_CAPTION_COST_GROUP"), 
                    align: "left", 
                    value: "costGroup",
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + '-header-cost-group'
                },
                {
                    text: this.$t("lang_GENERIC_CAPTION_DATE"), 
                    align: "right", 
                    value: "date",
                    width: "110px",
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + '-header-date'
                },                
                {
                    text: this.$t("lang_GENERIC_CAPTION_RECIPIENT"), 
                    align: "left", 
                    value: "recipient",
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + '-header-recipient'
                },
                {
                    text: this.$t("lang_LABEL_CONTRACTS_CAPTION_DESCRIPTION"), 
                    align: "left", 
                    value: "description",
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + '-header-description'
                },
                {
                    text: this.$t("lang_GENERIC_CAPTION_AMOUNT") + ' ' + this.$store.getters.getSelectedCurrency, 
                    align: "right", 
                    value: "amount",
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + '-header-amount'
                },
                {
                    text: this.$t("lang_LABEL_RELEASES_CAPTION_DOCUMENT_NUMBER"), 
                    align: "left", 
                    value: "documentNumber",
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + '-header-document-number'
                },
                {
                    text: this.$t('lang_GENERAL_CAPTION_COUNTRY'), 
                    value: 'country',
                    align: 'left', 
                    class: 'text-no-wrap',
                    width: '90px',
                    sortable: false,
                    alvqa: this.alvqa + '-header-country'
                },
                {
                    text: this.$t("lang_LABEL_CONTRACTS_CAPTION_PAID_ON"), 
                    align: "right", 
                    value: "paidOn",
                    width: "110px",
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + '-header-paid-on'
                },
                {
                    text: this.$t("lang_LABEL_CONTRACTS_CAPTION_ACCOUNTING_PERIOD"), 
                    align: "right", 
                    value: "accountingPeriod",
                    class: 'text-no-wrap',
                    width: "180px",
                    sortable: false,
                    alvqa: this.alvqa + '-header-accounting-period'
                },
                {
                    text: this.$t("lang_GENERAL_CAPTION_SPLIT") + ' %', 
                    align: "right", 
                    value: "split",
                    class: 'text-no-wrap',
                    width: "100px",
                    sortable: false,
                    alvqa: this.alvqa + '-header-split'
                }, 
                {
                    text: this.$t("lang_CONTRACT_LABEL_CAPTION_COST_RELEASE_RELATION"), 
                    align: "left", 
                    value: "assignToRelease",
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + '-header-assign-to-release'
                },
                {
                    text: '', 
                    value: 'inlineEdit',
                    align: 'right',
                    sortable: false,
                    width: "70px",
                    class: 'text-no-wrap'
                },
                {
                    text: "",
                    align: "right",
                    width: "70px",
                    value: "delete",
                    sortable: false,
                    class: 'text-no-wrap'
                }
            ]
        },

        listItems() { return this.$store.getters.getLabelContractsCosts },
        // Paging
        currentPage: {
            get() { return this.$store.getters.getLabelContractsCostsCurrentPage },
            set(page) { this.$store.commit('setLabelContractsCostsCurrentPage', page)}
        },
        numberOfPages() { return this.$store.getters.getLabelContractsCostsNumberOfPages },
        itemsPerPage() { return this.$store.getters.getItemsPerPage },
        totalRecords() { return this.$store.getters.getLabelContractsCostsTotalRecords },
        isLoading() { return this.$store.getters.getIsLoading },
    },
    methods: {
        getCostGroup(costGroupvalue) {
            let costGroupsTypes = DropdownLabelReleasesBudgetGroupTypes

            if(costGroupsTypes !== undefined) {
                let match = costGroupsTypes.find(type => {
                    return type.value === costGroupvalue
                })
                if(match !== undefined) {
                    if(match.group === 'income') return this.$t('lang_GENERIC_CAPTION_INCOME') + ': ' + this.$t(match.text)
                    if(match.group === 'costs') return this.$t('lang_LABEL_RELEASES_PANEL_CAPTION_COSTS') + ': ' + this.$t(match.text)
                } else return ''
            } else return ''
        },
        getAssignToRelease(assignToRelease) {
            if(assignToRelease !== null) {
                return assignToRelease.title + ' (' + this.$t('lang_LABEL_SALES_DATA_CAPTION_CAT_NO') +': ' + assignToRelease.catalogueNumber + ')' 
            } else return ''
        },
        deleteRow(item) {
            // set the selected row that should be deleted from the delete action 
            // and open the deletion dialog
            this.$store.commit("setSelectedLabelContractsCosts", item)
            this.$store.commit('setDeletionDialogContext', 'SelectedLabelContractsCosts')
            this.$store.commit('setShowDeletionDialog', true)
        },
        addNewRow() {
            this.$store.commit('setShowLabelContractsCostsEditingDialog', true)
            this.$store.dispatch('fetchAssignToReleaseDropdown')
            this.$store.commit('setEditingDialogHeadline', this.$t('lang_GENERAL_CAPTION_NEW_ENTRY'))
            let defaultValues = JSON.parse(JSON.stringify(this.$store.getters.getDefaultLabelContractsCosts)) 
            this.$store.commit('setLabelContractsCostsEditingDialogDetails', defaultValues)
        },
        editInlineFunction(item) {
            this.$store.commit('setSelectedLabelContractsCosts', item.id)
            let detailsDeepCopy = JSON.parse(JSON.stringify(item))

             
            detailsDeepCopy['amount'] = this.$n(detailsDeepCopy['amount'], 'numberWithoutGrouping')
            detailsDeepCopy['split'] = this.$n(detailsDeepCopy['split'], 'number')
            if(detailsDeepCopy.recipient !== null) this.$store.commit('setLabelContractsCostsRecipientOptions', [detailsDeepCopy.recipient])
            else this.$store.commit('setLabelContractsCostsRecipientOptions', [])

            // set the copy in dialog details to edit
            this.$store.commit('setLabelContractsCostsEditingDialogDetails', detailsDeepCopy)
            this.$store.commit('setEditingDialogHeadline', this.$t("lang_GENERAL_CAPTION_EDITING_ENTRY")) // set headline
            this.$store.dispatch('fetchAssignToReleaseDropdown')
            this.$store.commit('setShowLabelContractsCostsEditingDialog', true)
        },
        async nextPage() {
            await this.$store.dispatch('fetchLabelContractsCosts')
        },
    },
    mounted() {
        this.$store.commit('setCountries')
    }
}
</script>